import { useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { Link } from "react-router-dom";
import { Star, Play, Pause, Shuffle, Sparkles, Cloud, Music, Heart, X } from "lucide-react";
import { fetchSongs, resolveImage } from "@/lib/api";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.55 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ index, children, className }) => (
  <span className={`block overflow-hidden pb-1 ${className || ""}`}>
    <motion.span
      className="block"
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const layer1x = useTransform(sx, [-1, 1], [-24, 24]);
  const layer1y = useTransform(sy, [-1, 1], [-14, 14]);
  const layer2x = useTransform(sx, [-1, 1], [36, -36]);
  const layer2y = useTransform(sy, [-1, 1], [20, -20]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // Random song taster — no sign-in needed
  const [nowPlaying, setNowPlaying] = useState(null);
  const [paused, setPaused] = useState(false);
  const audioRef = useRef(null);

  const playRandom = async () => {
    const songs = await fetchSongs().catch(() => []);
    const playable = songs.filter((s) => s.audio_url);
    if (!playable.length) return;
    let next = playable[Math.floor(Math.random() * playable.length)];
    if (playable.length > 1 && nowPlaying) {
      while (next.song_id === nowPlaying.song_id) {
        next = playable[Math.floor(Math.random() * playable.length)];
      }
    }
    audioRef.current?.pause();
    const a = new Audio(resolveImage(next.audio_url));
    audioRef.current = a;
    a.onended = () => setNowPlaying(null);
    a.play().catch(() => {});
    setNowPlaying(next);
    setPaused(false);
  };

  const togglePause = () => {
    if (!audioRef.current) return;
    if (paused) {
      audioRef.current.play().catch(() => {});
      setPaused(false);
    } else {
      audioRef.current.pause();
      setPaused(true);
    }
  };

  const stopPlay = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setNowPlaying(null);
    setPaused(false);
  };

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden pt-32 pb-20"
      data-testid="hero-section"
    >
      {/* backdrop washes */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#FEF3C7] blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#E0F2FE] blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-[#DCFCE7] blur-3xl" />
      </div>

      {/* parallax floating universe */}
      <motion.div style={{ x: layer1x, y: layer1y }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <Star className="animate-float-soft absolute left-[8%] top-[22%] h-10 w-10 fill-[#FFC107] text-[#FFC107] drop-shadow-lg" />
        <Cloud className="animate-float-soft-delayed absolute right-[10%] top-[18%] h-16 w-16 fill-white text-white drop-shadow-md" />
        <Music className="animate-float-soft absolute bottom-[24%] left-[12%] h-8 w-8 text-[#9333EA]" />
      </motion.div>
      <motion.div style={{ x: layer2x, y: layer2y }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <Sparkles className="animate-float-soft-delayed absolute right-[16%] top-[38%] h-9 w-9 text-[#FF6B6B]" />
        <Heart className="animate-float-soft absolute left-[22%] top-[58%] h-7 w-7 fill-[#FF6B6B]/70 text-[#FF6B6B]" />
        <Cloud className="animate-float-soft absolute left-[38%] top-[14%] h-12 w-12 fill-white text-white drop-shadow-md" />
      </motion.div>
      <motion.div
        style={{ y: parallaxY }}
        className="rainbow-arc pointer-events-none absolute -bottom-40 left-1/2 h-[30rem] w-[54rem] -translate-x-1/2 opacity-70"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-[#FCD34D] bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 shadow-sm backdrop-blur"
          data-testid="hero-brand-badge"
        >
          <Star className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
          Sing &bull; Learn &bull; Play &bull; Grow
        </motion.div>

        <h1 className="font-display text-balance text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
          <MaskedLine index={0}>Little Songs.</MaskedLine>
          <MaskedLine index={1}>
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#FFC107] via-[#FF6B6B] to-[#9333EA] bg-clip-text text-transparent">
                Big Faith.
              </span>
            </span>
          </MaskedLine>
        </h1>

        <MaskedLine index={2} className="mt-7">
          <p className="mx-auto max-w-2xl text-base text-slate-600 leading-relaxed sm:text-lg" data-testid="hero-subheadline">
            Songs they&apos;ll love. Truths they&apos;ll remember. Christian songs, Bible
            adventures and joyful activities that help families make faith part of
            everyday life.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            onClick={playRandom}
            data-testid="hero-watch-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-[#FFC107] px-8 py-4 font-display text-lg font-semibold text-slate-900 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.45)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-8px_rgba(234,179,8,0.55)] active:translate-y-0"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-900 transition-transform group-hover:scale-110">
              {nowPlaying && !paused ? (
                <Pause className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
              ) : (
                <Play className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
              )}
            </span>
            {nowPlaying ? "Another one!" : "Watch & Sing"}
          </button>
          <Link
            to="/signin"
            state={{ mode: "signup" }}
            data-testid="hero-join-btn"
            className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-8 py-4 font-display text-lg font-semibold text-slate-900 transition-all hover:-translate-y-0.5 hover:bg-slate-900 hover:text-white"
          >
            Join Free
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-8 font-hand text-2xl text-slate-500"
          data-testid="hero-handwritten-note"
        >
          made for little hearts, loved by grown-ups
        </motion.p>
      </div>

      {/* random song mini player */}
      <AnimatePresence>
        {nowPlaying && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-[#E2DDD0] bg-white/95 py-2.5 pl-4 pr-3 shadow-2xl backdrop-blur"
            data-testid="hero-random-player"
          >
            <span
              className="grid h-9 w-9 place-items-center rounded-full text-white"
              style={{ backgroundColor: nowPlaying.color }}
            >
              <Music className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="max-w-44 truncate font-display text-sm font-semibold text-slate-900" data-testid="hero-random-title">
                {nowPlaying.title}
              </p>
              <p className="text-xs font-semibold text-slate-400">{nowPlaying.category} — a little taster</p>
            </div>
            <button
              onClick={togglePause}
              data-testid="hero-random-pause"
              aria-label={paused ? "Resume" : "Pause"}
              className="grid h-10 w-10 place-items-center rounded-full bg-[#F8F5EC] text-slate-700 transition-colors hover:bg-[#FEF3C7]"
            >
              {paused ? <Play className="ml-0.5 h-4 w-4 fill-current" /> : <Pause className="h-4 w-4 fill-current" />}
            </button>
            <button
              onClick={playRandom}
              data-testid="hero-random-shuffle"
              aria-label="Play another random song"
              className="grid h-10 w-10 place-items-center rounded-full bg-[#F8F5EC] text-slate-700 transition-colors hover:bg-[#FEF3C7]"
            >
              <Shuffle className="h-4 w-4" />
            </button>
            <button
              onClick={stopPlay}
              data-testid="hero-random-stop"
              aria-label="Stop"
              className="grid h-10 w-10 place-items-center rounded-full bg-[#F8F5EC] text-slate-400 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-9 w-6 rounded-full border-2 border-slate-300 p-1"
        >
          <div className="h-2 w-full rounded-full bg-[#FFC107]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
